/* eslint-disable consistent-return */
import clsx from 'clsx';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutocompleteField as AutocompleteFieldType,
  IAutocompleteComponent,
  ISelectOption,
} from '../../../types';
import Dropdown from '../../dropdown';
import useDropdown from '../../dropdown/hook';
import Unbutton from '../button/unbutton';
import Field from '../field';
import inputStyles from '../input/styles.module.css';

const filterDefault =
  (test: string) =>
  (opt: ISelectOption): boolean => {
    const lc = test.toLowerCase();
    return opt.value.toLowerCase().includes(lc) || opt.label.toLowerCase().includes(lc);
  };

const Option = ({
  option,
  RenderOption,
}: {
  option: ISelectOption;
  RenderOption?: FC<ISelectOption>;
}) => {
  if (!RenderOption) return <>{option.label}</>;

  return <RenderOption {...option} />;
};

const AutocompleteComponent: FC<IAutocompleteComponent> = props => {
  const {
    type = 'text',
    className = '',
    placeholder = 'Start typing...',
    iconLeft,
    iconRight,
    controlProps = {},
    fullwidth = true,
    showOnFocus = true,
    value,
    options,
    RenderOption,
    onSelect,
    filterOn = filterDefault,
    ...rest
  } = props;

  const iconsLeftClass = iconLeft ? 'has-icons-left' : '';
  const iconsRightClass = iconRight ? 'has-icons-right' : '';
  const fullwidthClass = fullwidth ? 'is-fullwidth' : '';
  const inputRef = useRef<HTMLInputElement>(null);
  const { id, isOpen, setIsOpen } = useDropdown();
  const [filter, setFilter] = useState('');
  const [hl, setHl] = useState(-1);
  const [selectedOption, setSelectedOption] = useState<ISelectOption | null>(null);

  const visibleOptions = useMemo(
    () => options.filter(filterOn(filter)),
    [options, filter, filterOn]
  );

  // If a "value" is passed, we search the options
  // list for the item with the same value and set it
  useEffect(() => {
    if (value && options.length > 0) {
      setFilter(options.find(opt => opt.value === value)?.label ?? '');
    }
  }, [value, options]);

  // helper for cancel action (close dropdown, reset hl)
  const handleCancel = () => {
    setFilter(selectedOption?.label ?? '');

    setIsOpen(false);
    setHl(-1);
  };

  const handleSelect = (option: ISelectOption | null) => {
    setFilter(option?.label ?? '');

    if (option !== selectedOption) {
      setSelectedOption(option);
      onSelect(option);
    }

    setIsOpen(false);
    setHl(-1);
  };

  const handleOnFocus = () => {
    if (showOnFocus) setIsOpen(true);
  };

  const handleMoveAway = () => {
    if (filter.length) {
      handleCancel();
    } else {
      // if the patient has deleted any input and moved away we assume
      // they want to deselect the current option
      handleSelect(null);
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    // 'Tab' is only caught on key down, not up
    if (e.key === 'Tab') {
      handleMoveAway();
    }
  };

  const handleOnKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      // take away focus
      inputRef.current?.blur();
      return handleCancel();
    }

    if (e.key === 'ArrowUp') {
      if (hl >= 0) return setHl(current => current - 1);
      return;
    }

    if (e.key === 'ArrowDown') {
      if (hl < visibleOptions.length - 1) return setHl(current => current + 1);
      return;
    }

    if (e.key === 'Enter') {
      if (hl === -1) return handleCancel();

      // select highlighted item
      const selected = visibleOptions[hl];
      handleSelect(selected);

      // also give away focus to be able to always show
      // the dropdown when input has focus and filter changes
      inputRef.current?.blur();
    }

    // do nothing.
  };

  return (
    <Dropdown id={id} isOpen={isOpen} setIsOpen={setIsOpen} onClickAway={handleMoveAway}>
      <div
        className={clsx('control', fullwidthClass, iconsLeftClass, iconsRightClass)}
        {...controlProps}>
        <input
          {...rest}
          // these properties are controlled by state and cannot be overriden
          ref={inputRef}
          type={type}
          className={clsx(inputStyles.input, fullwidthClass, className)}
          placeholder={placeholder}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          onKeyUp={handleOnKeyUp}
          onKeyDown={handleOnKeyDown}
          value={filter}
        />

        {/* handle possible icons */}
        {Boolean(iconLeft) && <span className="icon is-left">{iconLeft}</span>}

        {Boolean(iconRight) && <span className="icon is-right">{iconRight}</span>}
      </div>

      <Dropdown.Menu id={id} style={{ width: '100%' }}>
        {!visibleOptions.length && filter.length === 0 && (
          <Dropdown.Item>No results.</Dropdown.Item>
        )}

        {!visibleOptions.length && filter.length > 0 && (
          <Dropdown.Item>No results match &quot;{filter}&quot;</Dropdown.Item>
        )}

        {visibleOptions.length > 0 &&
          visibleOptions.map((option, i) => {
            const handler = () => {
              handleSelect(option);
            };

            return (
              <Dropdown.Item key={option.value} isHighlighted={hl === i}>
                <Unbutton
                  onClick={handler}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}>
                  <Option option={option} RenderOption={RenderOption} />
                </Unbutton>
              </Dropdown.Item>
            );
          })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const AutocompleteField: FC<AutocompleteFieldType> = props => {
  const {
    label,
    labelProps,
    fullwidth = true,
    required = false,
    errorMessage,
    ...autocompleteProps
  } = props;

  return (
    <Field
      label={label}
      labelProps={labelProps}
      fullwidth={fullwidth}
      required={required}
      errorMessage={errorMessage}>
      <AutocompleteComponent fullwidth={fullwidth} required={required} {...autocompleteProps} />
    </Field>
  );
};

export default {
  Component: AutocompleteComponent,
  Field: AutocompleteField,
};
